import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useApiAdminusersIdloginHistoryGetCollection } from '@r/api/generated/client.ts';
import type { UserHistoryLoginUserHistoryLoginReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type UserLoginHistoryTableProps = {
  userId: string;
  userUniqueCode: string;
};

export function UserLoginHistoryTable({ userId, userUniqueCode }: UserLoginHistoryTableProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const columns = useMemo<ColumnDef<UserHistoryLoginUserHistoryLoginReadModel>[]>(() => {
    return [
      {
        id: 'date',
        header: t('label.user.login.connected.date'),
        cell: ({ row }) =>
          row.original.connectedAt
            ? format(new Date(row.original.connectedAt), 'dd/MM/yyyy', { locale: fr })
            : ''
      },
      {
        id: 'time',
        header: t('label.user.login.connected.time'),
        cell: ({ row }) =>
          row.original.connectedAt
            ? format(new Date(row.original.connectedAt), 'HH:mm', { locale: fr })
            : ''
      },
      {
        id: 'uniqueCode',
        header: t('label.user.unique.code'),
        cell: () => userUniqueCode
      }
    ];
  }, [t, userUniqueCode]);

  const loginHistoryQuery = useApiAdminusersIdloginHistoryGetCollection(userId, {
    page: apiPagination.pageIndex + 1,
    perPage: apiPagination.pageSize
  });
  useOrvalQueryErrorToast(loginHistoryQuery, t('toast.error.label'));

  const data = useMemo<UserHistoryLoginUserHistoryLoginReadModel[]>(() => {
    return loginHistoryQuery.data?.data ?? [];
  }, [loginHistoryQuery.data]);

  const { table } = useDataTable<UserHistoryLoginUserHistoryLoginReadModel, unknown>({
    data,
    columns,
    pageCount: loginHistoryQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  useEffect(() => {
    const { pageIndex, pageSize } = table.getState().pagination;
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().pagination.pageIndex, table.getState().pagination.pageSize]);

  if (loginHistoryQuery.isLoading || loginHistoryQuery.isFetching) {
    return <DataTableSkeleton columnCount={3} cellWidths={['10rem', '10rem', '6rem']} />;
  }

  return <DataTable table={table} totalItems={loginHistoryQuery.data?.pagination?.totalItems} />;
}
